import React, { Dispatch, SetStateAction, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { QuoteDA } from '../../services/admin/quote.service';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { AppService } from '../../models/app.service.model';
import { Company } from '../../models/company.model';
import { Quote } from '../../models/quote.model';
import { legendInvalid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { evaluateLegendRequired } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLegend from '../element/Legend';

export interface AppModalQuoteNotificationProps {
  appServiceForUser: AppService | null,
  companyForUser: Company | null,
  quote: Quote | null,
  setQuote: Dispatch<SetStateAction<Quote | null>>
};

let errorResponse: Error, quoteResponse: Quote;

const AppModalQuoteNotification: React.FunctionComponent<AppModalQuoteNotificationProps> = ({appServiceForUser, companyForUser, quote, setQuote}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [comment, setComment] = useState({value: '', valid: false})

  const submitQuoteStatus = async () => {
    setLoadIndicator('on')

    if (appServiceForUser && companyForUser && quote && comment.valid) {
      let statusQuote = 'rejected'

      await QuoteDA.putQuoteStatus(companyForUser.id, quote.id, statusQuote, comment.value).then( async (response) => {
        if (response.status === 200) {
          quoteResponse = response.data

          setQuote(quoteResponse)

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedStatus,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalQuoteNotification()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!comment.value.length) legendInvalid('modal-quote-notification-legend-comment-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeComment = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setComment({...comment, value: event.target.value})
  }

  const validateComment = () => {
    evaluateLegendRequired(expressions.text, comment, setComment, 'modal-quote-notification-legend-comment-valid', 'modal-quote-notification-legend-comment-required')
  }

  const executeHideModalQuoteNotification = () => {
    modalHide('modal-quote-notification')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-quote-notification-legend-comment-valid')
    legendValidInvalidRestart('modal-quote-notification-legend-comment-required')
    setComment({value: '', valid: false})
  }

  return (
    <div id="modal-quote-notification" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.quoteNotification}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="text-center fs-6 mb-5">
                <h5>{lang.labels.youWantToChangeQuoteStatusRejected}</h5>
                <label>{lang.labels.youWillNotBeAbleToUndo}</label>
              </div>
              <div className="fv-row">
                <label className="form-label required">{lang.labels.message}</label>
                <TextareaAutosize className="form-control" name="comment" minRows={2} maxRows={3} value={comment.value} onChange={handleChangeComment} onKeyUp={validateComment} onBlur={validateComment} />
                <AppLegend component={"modal-quote-notification"} attribute={{check: comment.valid, name: "comment", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitQuoteStatus}>
              <span className="indicator-label">{lang.labels.yesReject}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalQuoteNotification}>{lang.labels.noCancel}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalQuoteNotification;
